import { useTranslation } from "react-i18next";
import { HelpCircle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useOnboarding } from "@/contexts/OnboardingContext";
import { supportWhatsAppUrl } from "@/lib/support";
import { cn } from "@/lib/utils";

/**
 * El botón de socorro de la cabecera durante la puesta en marcha: abre WhatsApp
 * con soporte. En pantalla ancha pone "Ayuda"; en el teléfono, donde no cabe,
 * "SOS".
 *
 * El mensaje va escrito de antemano con la empresa y el paso en el que está
 * atascado quien escribe, para que soporte no tenga que empezar preguntándolo.
 */
export function OnboardingHelpButton({ className }: { className?: string }) {
  const { t } = useTranslation();
  const { company } = useAuth();
  const { currentStep, completedCount, totalCount } = useOnboarding();

  const stepLabel = currentStep ? t(currentStep.title_key, currentStep.step_key) : null;
  const href = supportWhatsAppUrl(
    t("guide.help.whatsappMessage", {
      defaultValue: "Hola, necesito ayuda con la puesta en marcha de {{company}} (paso {{done}}/{{total}}: {{step}}).",
      company: company?.name ?? "—",
      done: completedCount + 1,
      total: totalCount,
      step: stepLabel ?? "—",
    }),
  );

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={t("guide.help.long", "Ayuda por WhatsApp")}
      className={cn(
        "inline-flex items-center gap-1.5 h-9 px-3 rounded-xl shrink-0",
        "bg-white/20 hover:bg-white/30 border border-white/30 text-white",
        "text-xs font-semibold active:scale-95 transition-all",
        className,
      )}
    >
      <HelpCircle className="w-4 h-4 shrink-0" />
      {/* Misma acción, dos anchos: "Ayuda" cuando cabe, "SOS" cuando no. */}
      <span className="hidden sm:inline">{t("guide.help.long", "Ayuda")}</span>
      <span className="sm:hidden">{t("guide.help.short", "SOS")}</span>
    </a>
  );
}
